import React, { useState } from 'react';
import { useSessions, useSession } from '../hooks/useData';
import { Session } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { X, ChevronRight, Globe, Search } from 'lucide-react';

export function Sessions() {
  const { sessions, loading } = useSessions();
  const [selected, setSelected] = useState<string | null>(null);
  const [search, setSearch]     = useState('');

  const filtered = sessions.filter(s => {
    const q = search.toLowerCase();
    return !q ||
      s.person?.toLowerCase().includes(q) ||
      s.company?.toLowerCase().includes(q) ||
      s.searchTerm?.toLowerCase().includes(q) ||
      s.sessionId.includes(q);
  });

  return (
    <div className="page" style={{ padding: 0, display: 'grid', gridTemplateColumns: selected ? '1fr 380px' : '1fr', height: '100%' }}>
      {/* ── List ─────────────────────────────────────────────────────── */}
      <div style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
            <input
              placeholder="Search visitors, companies, keywords..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', paddingLeft: 32, padding: '8px 12px 8px 32px',
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 8, color: 'var(--text)', fontSize: 13,
                fontFamily: 'var(--font)', outline: 'none',
              }}
            />
          </div>
          <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>
            {filtered.length} sessions
          </span>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px' }}>
          {loading ? (
            <div className="empty"><div className="empty-title">Loading sessions...</div></div>
          ) : filtered.length === 0 ? (
            <div className="empty">
              <Globe size={32} style={{ opacity: 0.3 }} />
              <div className="empty-title">No sessions yet</div>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>Embed tracker.js on a website to start capturing visitors</div>
            </div>
          ) : (
            <table style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th>Visitor</th>
                  <th>Score</th>
                  <th>Search term</th>
                  <th>Visit</th>
                  <th>Country</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <SessionRow
                    key={s.sessionId}
                    session={s}
                    selected={selected === s.sessionId}
                    onClick={() => setSelected(selected === s.sessionId ? null : s.sessionId)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Detail panel ─────────────────────────────────────────────── */}
      {selected && (
        <SessionDetail
          sessionId={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function SessionRow({ session: s, selected, onClick }: { session: Session; selected: boolean; onClick: () => void }) {
  return (
    <tr onClick={onClick} style={{ background: selected ? 'var(--bg2)' : undefined }}>
      <td>
        <div style={{ fontWeight: 600 }}>{s.person ?? s.company ?? <span style={{ color: 'var(--text3)', fontWeight: 400 }}>Anonymous</span>}</div>
        {s.person && s.company && <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{s.company}</div>}
      </td>
      <td>
        <ScoreBadge score={s.score} />
      </td>
      <td style={{ maxWidth: 160 }}>
        <span className="truncate" style={{ display: 'block', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>
          {s.searchTerm ?? '—'}
        </span>
      </td>
      <td className="mono" style={{ fontSize: 11 }}>#{s.visitNumber}</td>
      <td className="mono" style={{ fontSize: 11 }}>{s.country ?? '—'}</td>
      <td>
        {s.hasConsent
          ? <span className="badge badge-green">Consented</span>
          : <span className="badge badge-gray">Browsing</span>
        }
      </td>
      <td className="mono" style={{ fontSize: 11, color: 'var(--text3)' }}>
        {formatDistanceToNow(new Date(s.updatedAt), { addSuffix: true })}
      </td>
      <td><ChevronRight size={14} color="var(--text3)" /></td>
    </tr>
  );
}

function SessionDetail({ sessionId, onClose }: { sessionId: string; onClose: () => void }) {
  const { session, loading } = useSession(sessionId);

  if (loading) return (
    <div style={{ borderLeft: '1px solid var(--border)', padding: 24 }}>
      <div className="empty"><div className="empty-title">Loading...</div></div>
    </div>
  );

  if (!session) return null;

  const e = session.enrichment;
  const person  = e?.likelyPerson;
  const company = e?.company;
  const geo     = e?.geo;

  return (
    <div style={{ borderLeft: '1px solid var(--border)', overflowY: 'auto', background: 'var(--bg1)' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{person ? `${person.firstName} ${person.lastName}` : company?.name ?? 'Anonymous'}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 2 }}>{sessionId.slice(0, 16)}...</div>
        </div>
        <button className="btn" style={{ padding: 6 }} onClick={onClose}><X size={14} /></button>
      </div>

      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Score */}
        <div className="card" style={{ padding: '14px 16px' }}>
          <div className="card-title">Intent Score</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 36, fontWeight: 800, color: session.score >= 70 ? 'var(--green)' : session.score >= 40 ? 'var(--amber)' : 'var(--text3)' }}>
              {session.score}
            </span>
            <span style={{ color: 'var(--text3)', fontSize: 13 }}>/ 100</span>
          </div>
          <IntentBar label="Pricing interest" value={session.score} color="var(--green)" />
        </div>

        {/* Person */}
        {person && (
          <div className="card" style={{ padding: '14px 16px' }}>
            <div className="card-title">Identity</div>
            <DetailRow label="Name"    value={`${person.firstName} ${person.lastName}`} />
            <DetailRow label="Title"   value={person.title} />
            <DetailRow label="Email"   value={person.email} />
            {person.phone && <DetailRow label="Phone" value={person.phone} />}
          </div>
        )}

        {/* Company */}
        {company && (
          <div className="card" style={{ padding: '14px 16px' }}>
            <div className="card-title">Company</div>
            <DetailRow label="Name"       value={company.name} />
            <DetailRow label="Industry"   value={company.industry} />
            <DetailRow label="Employees"  value={company.employees?.toString()} />
            <DetailRow label="Location"   value={`${company.city}, ${company.country}`} />
            {company.techUsed?.length > 0 && (
              <DetailRow label="Tech stack" value={company.techUsed.slice(0, 3).join(', ')} />
            )}
          </div>
        )}

        {/* Behavior */}
        <div className="card" style={{ padding: '14px 16px' }}>
          <div className="card-title">Behavior</div>
          <DetailRow label="Search term"  value={session.searchTerm ?? '—'} />
          <DetailRow label="Visit #"      value={`#${session.visitNumber}`} />
          <DetailRow label="Time on site" value={`${Math.round(session.duration / 1000)}s`} />
          <DetailRow label="Max scroll"   value={`${session.maxScroll}%`} />
          <DetailRow label="Page intent"  value={session.pageIntent ?? '—'} />
          <DetailRow label="Country"      value={geo?.countryName ?? session.country ?? '—'} />
        </div>

        {/* Consents */}
        {session.consents && session.consents.length > 0 && (
          <div className="card" style={{ padding: '14px 16px' }}>
            <div className="card-title">Consents</div>
            {session.consents.map((c: any, i: number) => (
              <DetailRow key={i} label={c.channel} value={formatDistanceToNow(new Date(c.recordedAt), { addSuffix: true })} />
            ))}
          </div>
        )}

        {/* Signals timeline */}
        {session.signals && session.signals.length > 0 && (
          <div className="card" style={{ padding: '14px 16px' }}>
            <div className="card-title">Signal Timeline</div>
            <div className="timeline">
              {session.signals.slice(-10).reverse().map((sig: any, i: number) => (
                <div className="timeline-item" key={i}>
                  <div className="timeline-dot" style={{ background: signalColor(sig.type) }} />
                  <div className="timeline-time">{Math.round(sig.ts / 1000)}s</div>
                  <div className="timeline-text">{signalLabel(sig)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function IntentBar({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="intent-bar" style={{ marginTop: 8, marginBottom: 0 }}>
      <div className="intent-bar-header">
        <span>{label}</span><span>{value}%</span>
      </div>
      <div className="intent-bar-track">
        <div className="intent-bar-fill" style={{ width: `${value}%`, background: color }} />
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value?: string }) {
  if (!value || value === '—') return null;
  return (
    <div className="detail-row">
      <span className="detail-key">{label}</span>
      <span className="detail-value truncate" style={{ maxWidth: 180 }}>{value}</span>
    </div>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 70 ? 'var(--green)' : score >= 40 ? 'var(--amber)' : 'var(--text3)';
  return <span style={{ color, fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 700 }}>{score}</span>;
}

function signalColor(type: string): string {
  const m: Record<string, string> = {
    pageview: '#6366f1', scroll: '#0ea5e9', click: '#f59e0b',
    hover: '#a855f7', exit_intent: '#ef4444', form_focus: '#22c55e',
  };
  return m[type] ?? '#71717a';
}

function signalLabel(sig: any): string {
  switch (sig.type) {
    case 'pageview':    return `Viewed ${sig.path ?? 'page'}`;
    case 'scroll':      return `Scrolled ${sig.depth}%`;
    case 'click':       return `Clicked: ${sig.text?.slice(0, 40) ?? 'element'}`;
    case 'hover':       return `Hovered ${sig.element} (${Math.round((sig.dwell_ms ?? 0) / 1000)}s)`;
    case 'exit_intent': return 'Exit intent detected';
    case 'form_focus':  return `Focused on ${sig.field ?? 'form field'}`;
    case 'video_play':  return 'Started watching video';
    case 'rage_click':  return 'Rage click (frustration)';
    default:            return sig.type;
  }
}
